<template>
  <div class="dashboard-container">
    <!-- Header -->
    <header class="dashboard-header">
      <div class="container">
        <div class="header-content">
          <div class="logo-section">
            <h1 class="logo-text">Daily Timeline</h1>
          </div>
          <nav class="nav-section">
            <div class="user-info">
              <span class="user-email">{{ userEmail }}</span>
            </div>
            <router-link :to="{ name: 'Logout' }" class="btn-logout">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="16 17 21 12 16 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Logout
            </router-link>
          </nav>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="dashboard-main">
      <div class="container">
        <!-- Welcome Section -->
        <div class="welcome-section">
          <h2 class="welcome-title">{{ timelines.length === 0 ? 'Welcome!' : 'Your Timelines' }}</h2>
          <p class="welcome-subtitle">Create and manage your personal memory timelines</p>
        </div>

        <!-- Create New Timeline Button -->
        <div class="create-timeline-section">
          <button @click="showCreateModal = true" class="btn-create-timeline">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            Create New Timeline
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>Loading your timelines...</p>
        </div>

        <!-- Timelines Grid -->
        <div v-else-if="timelines.length > 0" class="timelines-grid">
          <div 
            v-for="timeline in timelines" 
            :key="timeline.id" 
            class="timeline-card"
            @click="goToTimeline(timeline.id)"
          >
            <div class="timeline-card-header">
              <div class="timeline-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="timeline-actions">
                <button @click.stop="editTimeline(timeline)" class="btn-edit" title="Edit timeline">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button @click.stop="deleteTimeline(timeline.id)" class="btn-delete" title="Delete timeline">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="timeline-card-body">
              <h3 class="timeline-name">{{ timeline.name }}</h3>
              <p class="timeline-description">{{ timeline.description || 'No description' }}</p>
              <div class="timeline-meta">
                <span class="timeline-date">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <polyline points="12 6 12 12 16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  Created {{ formatDate(timeline.created_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Create Timeline Modal -->
    <transition name="modal">
      <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
        <div class="modal-content">
          <div class="modal-header">
            <h3>Create New Timeline</h3>
            <button @click="showCreateModal = false" class="btn-close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <form @submit.prevent="createTimeline" class="modal-form">
            <div class="form-group">
              <label for="timelineName" class="form-label">Timeline Name</label>
              <input 
                type="text" 
                id="timelineName" 
                v-model="newTimeline.name"
                class="form-input"
                placeholder="e.g., Family Memories, 2024 Adventures"
                required
                maxlength="255"
              >
            </div>
            <div class="form-group">
              <label for="timelineDescription" class="form-label">Description (Optional)</label>
              <textarea 
                id="timelineDescription" 
                v-model="newTimeline.description"
                class="form-textarea"
                placeholder="What is this timeline about?"
                rows="4"
              ></textarea>
            </div>
            <div v-if="createError" class="error-message">
              {{ createError }}
            </div>
            <div class="modal-actions">
              <button type="button" @click="showCreateModal = false" class="btn-cancel">
                Cancel
              </button>
              <button type="submit" class="btn-submit" :disabled="creating">
                {{ creating ? 'Creating...' : 'Create Timeline' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Edit Timeline Modal -->
    <transition name="modal">
      <div v-if="showEditModal" class="modal-overlay" @click.self="showEditModal = false">
        <div class="modal-content">
          <div class="modal-header">
            <h3>Edit Timeline</h3>
            <button @click="showEditModal = false" class="btn-close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <form @submit.prevent="updateTimeline" class="modal-form">
            <div class="form-group">
              <label for="editTimelineName" class="form-label">Timeline Name</label>
              <input 
                type="text" 
                id="editTimelineName" 
                v-model="editingTimeline.name"
                class="form-input"
                placeholder="e.g., Family Memories, 2024 Adventures"
                required
                maxlength="255"
              >
            </div>
            <div class="form-group">
              <label for="editTimelineDescription" class="form-label">Description (Optional)</label>
              <textarea 
                id="editTimelineDescription" 
                v-model="editingTimeline.description"
                class="form-textarea"
                placeholder="What is this timeline about?"
                rows="4"
              ></textarea>
            </div>
            <div v-if="editError" class="error-message">
              {{ editError }}
            </div>
            <div class="modal-actions">
              <button type="button" @click="showEditModal = false" class="btn-cancel">
                Cancel
              </button>
              <button type="submit" class="btn-submit" :disabled="updating">
                {{ updating ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Dashboard',
  data() {
    return {
      timelines: [],
      loading: true,
      showCreateModal: false,
      creating: false,
      createError: null,
      newTimeline: {
        name: '',
        description: ''
      },
      showEditModal: false,
      updating: false,
      editError: null,
      editingTimeline: {
        id: null,
        name: '',
        description: ''
      }
    };
  },
  computed: {
    userId() {
      return this.$store.getters.userID;
    },
    userEmail() {
      // Try to get from stored user profile, fallback to localStorage
      try {
        const user = this.$store.getters.user;
        if (user && user.email) {
          return user.email;
        }
      } catch (e) {
        // User data not loaded yet
      }
      return localStorage.getItem('user_email') || 'User';
    }
  },
  mounted() {
    // Check if user is authenticated
    const userId = this.$store.getters.userID;
    
    if (!userId) {
      // Try auto login first
      this.$store.dispatch('tryAutoLogin');
      
      // Check again after a short delay
      setTimeout(() => {
        const userIdAfterLogin = this.$store.getters.userID;
        if (userIdAfterLogin) {
          this.loadUserAndTimelines();
        } else {
          // No user logged in, redirect to login
          this.$router.push('/login');
        }
      }, 100);
    } else {
      // User is already authenticated
      this.loadUserAndTimelines();
    }
  },
  methods: {
    loadUserAndTimelines() {
      // Fetch user data if not already loaded
      if (!this.$store.getters.user) {
        this.$store.dispatch('getUser').then(() => {
          this.fetchTimelines();
        }).catch(() => {
          // If getUser fails, still try to fetch timelines
          this.fetchTimelines();
        });
      } else {
        this.fetchTimelines();
      }
    },
    fetchTimelines() {
      if (!this.userId) {
        console.error('No user ID available');
        this.loading = false;
        return;
      }
      
      this.loading = true;
      axios.get(`/api/timelines/?user=${this.userId}`)
        .then(response => {
          this.timelines = response.data;
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching timelines:', error);
          this.loading = false;
        });
    },
    createTimeline() {
      if (!this.newTimeline.name.trim()) {
        this.createError = 'Timeline name is required';
        return;
      }

      this.creating = true;
      this.createError = null;
      
      axios.post('/api/timelines/', {
        user: this.userId,
        name: this.newTimeline.name,
        description: this.newTimeline.description
      })
        .then(response => {
          // Add new timeline to the list
          this.timelines.unshift(response.data);
          
          // Reset form and close modal
          this.newTimeline = { name: '', description: '' };
          this.showCreateModal = false;
          this.creating = false;
        })
        .catch(error => {
          console.error('Error creating timeline:', error);
          this.createError = 'Failed to create timeline. Please try again.';
          this.creating = false;
        });
    },
    editTimeline(timeline) {
      this.editingTimeline = {
        id: timeline.id,
        name: timeline.name,
        description: timeline.description || ''
      };
      this.editError = null;
      this.showEditModal = true;
    },
    updateTimeline() {
      if (!this.editingTimeline.name.trim()) {
        this.editError = 'Timeline name is required';
        return;
      }

      this.updating = true;
      this.editError = null;
      
      axios.patch(`/api/timelines/${this.editingTimeline.id}/`, {
        name: this.editingTimeline.name,
        description: this.editingTimeline.description
      })
        .then(response => {
          // Update the timeline in the list
          const index = this.timelines.findIndex(t => t.id === this.editingTimeline.id);
          if (index !== -1) {
            this.timelines.splice(index, 1, response.data);
          }
          
          // Close modal
          this.showEditModal = false;
          this.updating = false;
        })
        .catch(error => {
          console.error('Error updating timeline:', error);
          this.editError = 'Failed to update timeline. Please try again.';
          this.updating = false;
        });
    },
    deleteTimeline(timelineId) {
      if (!confirm('Are you sure you want to delete this timeline? This action cannot be undone.')) {
        return;
      }

      axios.delete(`/api/timelines/${timelineId}/`)
        .then(() => {
          this.timelines = this.timelines.filter(t => t.id !== timelineId);
        })
        .catch(error => {
          console.error('Error deleting timeline:', error);
          alert('Failed to delete timeline. Please try again.');
        });
    },
    goToTimeline(timelineId) {
      this.$router.push({
        name: 'timeline',
        query: { 
          userID: this.userId,
          timelineID: timelineId 
        }
      });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return 'today';
      } else if (diffDays === 1) {
        return 'yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
      }
    }
  }
};
</script>

<style scoped>
/* Container */
.dashboard-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eaf0 100%);
}

/* Header */
.dashboard-header {
  background: linear-gradient(135deg, #702670 0%, #8e2f8e 100%);
  color: white;
  padding: 20px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-text {
  font-family: 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive;
  font-size: 2rem;
  font-weight: normal;
  color: white;
  font-style: italic;
  letter-spacing: 1px;
  margin: 0;
}

.nav-section {
  display: flex;
  align-items: center;
  gap: 24px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-email {
  font-size: 14px;
  opacity: 0.95;
}

.btn-logout {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-logout:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white;
  text-decoration: none;
  transform: translateY(-1px);
}

/* Main Content */
.dashboard-main {
  padding: 60px 0;
}

.welcome-section {
  text-align: center;
  margin-bottom: 40px;
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1a202c;
  margin: 0 0 12px 0;
}

.welcome-subtitle {
  font-size: 1.1rem;
  color: #718096;
  margin: 0;
}

/* Create Timeline Section */
.create-timeline-section {
  display: flex;
  justify-content: center;
  margin-bottom: 48px;
}

.btn-create-timeline {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #702670 0%, #8e2f8e 100%);
  color: white;
  padding: 16px 32px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(112, 38, 112, 0.3);
}

.btn-create-timeline:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(112, 38, 112, 0.4);
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #718096;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top-color: #702670;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Timelines Grid */
.timelines-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.timeline-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
}

.timeline-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(112, 38, 112, 0.15);
}

.timeline-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.timeline-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #f8e6f8 0%, #e8d0e8 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #702670;
}

.timeline-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-edit {
  background: transparent;
  border: none;
  color: #702670;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.btn-edit:hover {
  background: #f8e6f8;
}

.btn-delete {
  background: transparent;
  border: none;
  color: #e53e3e;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.btn-delete:hover {
  background: #fff5f5;
}

.timeline-card-body {
  display: flex;
  flex-direction: column;
}

.timeline-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 8px 0;
  word-break: break-word;
}

.timeline-description {
  font-size: 0.95rem;
  color: #718096;
  margin: 0 0 16px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.timeline-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.timeline-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: #a0aec0;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.btn-close {
  background: transparent;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: #f7fafc;
  color: #2d3748;
}

.modal-form {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  transition: all 0.3s ease;
  font-family: inherit;
  background-color: #f7fafc;
}

.form-input:focus,
.form-textarea:focus {
  border-color: #702670;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(112, 38, 112, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.error-message {
  background: #fff5f5;
  border: 1px solid #fc8181;
  color: #c53030;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel,
.btn-submit {
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.btn-cancel {
  background: #f7fafc;
  color: #4a5568;
}

.btn-cancel:hover {
  background: #e2e8f0;
}

.btn-submit {
  background: linear-gradient(135deg, #702670 0%, #8e2f8e 100%);
  color: white;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(112, 38, 112, 0.3);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .logo-text {
    font-size: 1.5rem;
  }

  .user-email {
    display: none;
  }

  .btn-logout {
    padding: 8px 16px;
    font-size: 13px;
  }

  .welcome-title {
    font-size: 2rem;
  }

  .welcome-subtitle {
    font-size: 1rem;
  }

  .timelines-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .modal-content {
    margin: 20px;
  }
}

@media (max-width: 480px) {
  .dashboard-main {
    padding: 40px 0;
  }

  .welcome-title {
    font-size: 1.75rem;
  }

  .btn-create-timeline {
    padding: 14px 24px;
    font-size: 15px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard.vue